<template>
  <div class="tags-container">
    <select class="single selectize selectize-bordered selectized" :class="multiple ? 'selectize-multiple' : 'selectize-single'" :name="selectedTagsInputName" :multiple="multiple"></select>
  </div>
</template>

<style scoped>
  .tags {
    margin-top: 20px;
  }
  .selectize-tags.selectize-control .selectize-input:after {
      position: absolute;
      cursor: pointer;
      background-color: #1a1a1a;
      width: 46px;
      height: 46px;
      top: 0;
      right: 0;
      margin: 0;
      border: none;
      content: "\f067";
      font-family: FontAwesome;
      color: #fff;
      font-size: 20px;
      text-align: center;
      padding: 11px 0;
  }

</style>

<script>
import 'selectize'
import { each, map, find, filter, trim, assign } from 'lodash'
import $ from 'jquery'

export default {
  props: {
    value: {
      required: false
    },
    type: {
      default: ''
    },
    creationType: {
      required: false,
      default: null
    },
    allowCreate: {
      required: false,
      default: true
    },
    aliases: {
      required: false,
      default: 'false'
    },
    id: {
      required: false,
      default: null
    },
    multiple: {
      required: false,
      default: true
    },
    tagType: {
      required: true
    }
  },

  data () {
    return {
      selectize: null,
      selectedTags: [],
      availableTags: []
    }
  },

  watch: {
    /**
     * Watch changes to the selected tags so we can emit it on the built-in "input" event
     * which should push changes to any v-models.
     *
     * @return void
     */
    selectedTags () {
      this.$emit('input', this.selectedTags)
    }
  },

  /**
   * On mounted we bind Selectize to the input and add any pre-selected tags.
   *
   * @return void
   */
  mounted () {
    this.bind()

    this.addPreSelected()
  },

  computed: {
    /**
     * Determine if we have selected tags.
     *
     * @return bool
     */
    haveSelectedTags () {
      return this.selectedTags.length > 0
    },

    /**
     * The input field name for the selected tags.
     *
     * @return string
     */
    selectedTagsInputName () {
      if (this.type) {
        return `${this.type}_tags[]`
      }

      return 'tags[]'
    }
  },

  methods: {
    /**
     * Bind Selectize to the DOM.
     *
     * @return void
     */
    bind () {
      this.selectize = $(this.$el).find('select').selectize({
        plugins: ['remove_button'],
        options: map(this.value, this.mapTagResponseForSelectize),
        preload: true,
        persist: true,
        valueField: 'id',
        labelField: 'name',
        searchField: 'name',

        /**
         * Create a new tag by posting to the server.
         *
         * @return void
         */
        create: this.allowCreate === false ? undefined : (name, callback) => {
          this.creationType = this.creationType ? this.creationType : this.type
          const payload = {
            name,
            type: this.creationType
          }

          this.$http.post('tags', payload)
            .then((response) => {
              let tag = this.mapTagResponseForSelectize(response.data)

              this.addAvailableTag(tag)

              callback(tag)
            })
        },
        /**
         * Filter out tags with exact matches for both the upper/lowercase versions.
         *
         * @return bool
         */
        createFilter: (tag) => {
          tag = trim(tag)

          // Compare the lowercase of existing available tags against the search term when creating
          // tags.
          if (find(this.availableTags, existing => existing.name.toLowerCase() === tag.toLowerCase()) !== undefined) {
            return false
          }

          // Do the same against the selected tags so the user can't type out a lower case selected
          // tag and add it to the database.
          if (find(this.selectedTags, selected => selected.name.toLowerCase() === tag.toLowerCase()) !== undefined) {
            return false
          }

          return true
        },
        /**
         * When an item is added when make sure it's in the array of available tags.
         * This then adds it to the selected tags so we can render them outside of Selectize.
         *
         * @return void
         */
        onItemAdd: (value) => {
          let tag = find(this.availableTags, (tag) => tag.id === parseInt(value))
          if (tag.pivot === undefined) {
            tag.pivot = {retired: null}
          }

          if (tag) {
            this.addSelectedTag(tag)
          }
        },
        /**
         * When an item is removed, trigger remove tag method so that changes will be emitted to parent component
         *
         * @return void
         */
        onItemRemove: value => {
          let tag = find(this.selectedTags, tag => tag.id === parseInt(value))

          if (tag) {
            this.removeTag(tag)
          }
        },
        /**
         * When typing into the Selectize field we'll poll the server for results. The results
         * are then assigned to the available tags array.
         *
         * @return void
         */
        load: (term, callback) => {
          let params = {
            term,
            type: this.type,
            aliases: this.aliases,
            tag_id: this.id,
            sort: 'name',
            status: 'active',
            tagType: this.tagType
          }
          if (this.tagType === 'interest-categories' || this.tagType === 'personal-interests') {
            params.status = 'all'
            this.$http.get(this.tagType, { params })
              .then(response => {
                let tags = map(response.data.data, this.mapTagResponseForSelectize)
                assign(this.availableTags, tags)
                callback(tags)
              })
          } else {
            // Tags of type description are used to characterize bookings
            // For example, 'female' tag simply means that the booking is
            // requiring a female talent. Description tags aren't tagged
            // against talent so we need to remove them if the type is not description.
            if (this.type === 'description') {
              params.type = 'all'
            }
            this.$http.get(this.type === 'opportunities' ? 'opportunities' : 'tags', { params })
              .then(response => {
                if (this.type === 'all') {
                  response.data.data = filter(response.data.data, tag => tag.type !== 'description')
                }
                let tags = map(response.data.data, this.mapTagResponseForSelectize)
                assign(this.availableTags, tags)
                callback(tags)
              })
          }
        },
        onChange: () => {
          this.selectize.$dropdown_content.removeHighlight()
        },
        onType: (string) => {
          if (!string) {
            this.selectize.$dropdown_content.removeHighlight()
          }
        },
        onDelete: () => {
          setTimeout(() => {
            this.selectize.$dropdown_content.removeHighlight()
          }, 0)
        }
      })[0].selectize
    },

    /**
     * Map a tag response to a format Selectize expects.
     *
     * @return object
     */
    mapTagResponseForSelectize (tag) {
      if (tag.pivot) {
        return { id: tag.id, name: tag.name, pivot: {retired: tag.pivot.retired} }
      } else {
        return { id: tag.id, name: tag.name, pivot: {retired: false} }
      }
    },

    /**
     * Add the pre-selected tags to both available tags and selected tags.
     *
     * @return void
     */
    addPreSelected () {
      each(map(this.value, this.mapTagResponseForSelectize), (tag) => {
        if (!this.tagAlreadyAvailable(tag)) {
          this.addAvailableTag(tag)
        }

        if (!this.tagAlreadySelected(tag)) {
          this.addSelectedTag(tag)
        }
      })
    },

    /**
     * Determine if a tag is already selected. Tag can be an integer or object.
     *
     * @param int|object tag
     *
     * @return bool
     */
    tagAlreadySelected (tag) {
      let id = tag.id || parseInt(tag)

      return find(this.selectedTags, (existing) => existing.id === id) !== undefined
    },

    /**
     * Determine if a tag is already available. Tag can be an integer or object.
     *
     * @param int|object tag
     *
     * @return bool
     */
    tagAlreadyAvailable (tag) {
      let id = tag.id || parseInt(tag)

      return find(this.availableTags, (existing) => existing.id === id) !== undefined
    },

    /**
     * Add a new selected tag.
     *
     * @param object tag
     */
    addSelectedTag (tag) {
      if (!this.tagAlreadySelected(tag)) {
        if (this.multiple) {
          this.selectedTags.push(tag)

          this.selectize.addItem(tag.id)
        } else {
          this.selectedTags = [tag]
          this.selectize.clear()
          this.selectize.addItem(tag.id)
        }
      }
    },

    /**
     * Add a new available tag.
     *
     * @param object tag
     */
    addAvailableTag (tag) {
      if (!this.tagAlreadyAvailable(tag)) {
        this.availableTags.push(tag)
      }
    },

    /**
     * Remove a tag from the selected tags.
     *
     * @param object tag
     *
     * @return void
     */
    removeTag (tag) {
      this.selectedTags = filter(this.selectedTags, existing => existing.id !== tag.id)

      this.selectize.removeItem(tag.id)
    }
  }
}
</script>
